import { useState } from "react";
import AdminModal from "../../../core/admin/adminModal";
import { useWatches } from "../../../../context/watchProvider";
import { IWatch } from "../watches/adminWatches";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminButton from "../../../core/admin/adminButton";
import { formatPhoneNumber } from "../../../../utils/functions";
import AdminCard from "../../../core/admin/adminCard";
import AdminTable from "../../../core/admin/adminTable";
import AdminField from "../../../core/admin/adminField";
import AdminDetails from "../../../core/admin/adminDetails";
import AdminConfirm from "../../../core/admin/adminConfirm";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminCount } from "../../../core/admin/adminBadge";
import { SearchLineIcon } from "../../../../img/svg/adminIcons";
import BuyerScoreCard from "../../../core/buyerScoreCard";
import { IBuyerScore } from "../../../../utils/buyerScore";

export default function AdvertisedWatches({ watches, refresh }: { watches: IWatch[], refresh: () => void }) {

    const { updateWatch, getUserByEmail, getBuyerScore } = useWatches();

    const [confirmModal, setConfirmModal] = useState(false);
    const [watch, setWatch] = useState<IWatch | null>(null);
    const [status, setStatus] = useState("");
    const [email, setEmail] = useState("");

    const [userLoading, setUserLoading] = useState(false);
    const [userFeedback, setUserFeedback] = useState("");
    const [foundUser, setFoundUser] = useState<{ id: string, name: string, email: string, phoneNumber: string } | null>(null);
    const [buyerScore, setBuyerScore] = useState<IBuyerScore | null>(null);
    const [buyerScoreLoading, setBuyerScoreLoading] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [saving, setSaving] = useState(false);

    const openConfirmModal = (w: IWatch) => {
        setConfirm(false);
        setFoundUser(null);
        setBuyerScore(null);
        setUserFeedback("");
        setUserLoading(false);
        setEmail("");
        setWatch(w);
        setConfirmModal(true);
    }

    const doMoveWatch = async (watch: IWatch | null) => {
        if (!watch?.id) {
            return null;
        }
        // relógio escondido deixa de ser banner (mesmo campo que doSellWatch já zera)
        const response = await updateWatch(watch.id, { status, isBanner: false });
        if (response) {
            refresh();
        }
    }

    const doSellWatch = async (watch: IWatch | null, userId: string) => {
        if (!watch?.id) {
            return null;
        }
        const response = await updateWatch(watch.id, { status, buyer: userId, isBanner: false, soldAt: new Date() });
        if (response) {
            refresh();
        }
    }

    const searchByEmail = async () => {
        try {
            setUserLoading(true);
            setUserFeedback("");
            setFoundUser(null);
            setBuyerScore(null);
            const user = await getUserByEmail(email);
            if (!user) {
                setUserFeedback("Usuário não encontrado");
                setUserLoading(false);
                return null;
            }
            setFoundUser(user);
            setUserLoading(false);
            setBuyerScoreLoading(true);
            const score = await getBuyerScore(user.id);
            setBuyerScore(score);
            setBuyerScoreLoading(false);
        } catch (error) {
            console.log(error)
            setUserLoading(false);
            setBuyerScoreLoading(false);
            setUserFeedback("Erro ao buscar usuário");
        }
    }

    // envolve as duas ações para exibir carregamento no botão (a lista recarrega ao final)
    const run = async (action: () => Promise<any>) => {
        setSaving(true);
        await action();
        setSaving(false);
    }

    return (
        <AdminCard title={<>Em exibição <AdminCount count={watches.length} /></>} description="Relógios visíveis no site. Marque como vendido ou esconda quando necessário." bodyClassName="p-3 md:p-4">
            {
                watches.length ?
                    <AdminTable
                        rows={watches}
                        rowKey={(w, idx) => w.id || idx}
                        columns={[
                            { header: "Relógio", mobile: "title", desktopHidden: true, render: w => `${w.brand || ""} ${w.model || ""}` },
                            { header: "Marca", mobile: "hidden", render: w => <span className="font-semibold">{w.brand}</span> },
                            { header: "Modelo", mobile: "hidden", render: w => w.model },
                            { header: "Ano", render: w => w.year },
                        ]}
                        actions={w => <AdminButton size="sm" variant="secondary" onClick={() => openConfirmModal(w)}>Atualizar</AdminButton>}
                    />
                    :
                    <AdminEmptyState title="Nenhum relógio listado" />
            }
            {
                confirmModal ?
                    <AdminModal
                        close={() => setConfirmModal(false)}
                        title="O que você deseja fazer com este relógio?"
                        subtitle={`${watch?.brand || ""} ${watch?.model || ""} · ${watch?.year || ""}`}
                        footer={
                            status === "sold" ?
                                <AdminButton variant="secondary" disabled={saving} onClick={() => setConfirmModal(false)}>Cancelar</AdminButton>
                                :
                                <>
                                    <AdminButton variant="secondary" disabled={saving} onClick={() => setConfirmModal(false)}>Cancelar</AdminButton>
                                    <AdminButton loading={saving} onClick={() => run(() => doMoveWatch(watch))} disabled={status !== "hidden"}>Salvar</AdminButton>
                                </>
                        }
                    >
                        <div className="flex flex-col gap-3">
                            <OptionCard id="sold" checked={status === "sold"} onChange={setStatus} title="Marcar como vendido" description="Informe o comprador pelo e-mail cadastrado na plataforma." />
                            {
                                status === "sold" ?
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-col gap-4">
                                        <AdminField label="Informe o comprador" htmlFor="buyer-email">
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <AdminTextInput id="buyer-email" type="email" placeholder="comprador@email.com" value={email} onChange={e => setEmail(e.target.value)} onEnter={email === "" ? undefined : searchByEmail} />
                                                <AdminButton variant="secondary" icon={<SearchLineIcon width="16px" />} loading={userLoading} disabled={email === ""} onClick={searchByEmail}>Buscar</AdminButton>
                                            </div>
                                        </AdminField>
                                        {
                                            userFeedback ?
                                                <p className="text-sm font-semibold text-red-600">{userFeedback}</p>
                                                : null
                                        }
                                        {
                                            foundUser ?
                                                <div className="flex flex-col gap-3">
                                                    <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200">
                                                        <AdminDetails
                                                            columns={3}
                                                            items={[
                                                                { label: "Nome", value: foundUser?.name },
                                                                { label: "E-mail", value: foundUser?.email },
                                                                { label: "Telefone", value: formatPhoneNumber(foundUser?.phoneNumber) },
                                                            ]}
                                                        />
                                                    </div>
                                                    <BuyerScoreCard buyerScore={buyerScore} loading={buyerScoreLoading} variant="compact" />
                                                    {
                                                        !confirm ?
                                                            <AdminButton variant="success" onClick={() => setConfirm(true)} className="self-end">Confirmar venda</AdminButton>
                                                            :
                                                            <AdminConfirm message="Tem certeza?" cancelLabel="Cancelar" loading={saving} onConfirm={() => run(() => doSellWatch(watch, foundUser.id))} onCancel={() => setConfirm(false)} />
                                                    }
                                                </div>
                                                : null
                                        }
                                    </div>
                                    : null
                            }
                            <OptionCard id="hidden" checked={status === "hidden"} onChange={setStatus} title="Esconder relógio" description="O relógio deixa de aparecer no site e vai para “Escondidos”." />
                        </div>
                    </AdminModal>
                    : null
            }
        </AdminCard>
    )
}

// opção de escolha única em formato de card (radio)
function OptionCard({ id, checked, onChange, title, description }: { id: string, checked: boolean, onChange: (v: string) => void, title: string, description: string }) {
    return (
        <label htmlFor={id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${checked ? "border-gray-900 bg-gray-50 ring-1 ring-gray-900" : "border-gray-200 hover:border-gray-300"}`}>
            <input type="radio" name="advertised-action" value={id} id={id} onChange={e => onChange(e.target.value)} checked={checked} className="mt-1 h-4 w-4 accent-gray-900" />
            <span>
                <span className="block font-semibold text-gray-900">{title}</span>
                <span className="block text-sm text-gray-500">{description}</span>
            </span>
        </label>
    )
}
